import React, { FC, useEffect, useState } from 'react';
import * as Styled from './DiagnosticsStepsImages.styles';
import * as O from 'fp-ts/Option';
import { DiagnosticsStepImages } from '@modules/diagnostics/model';
import Glide from '@glidejs/glide';
import Image from '@shared/modules/resources/components/Image';
import NextImage from 'next/image';
import arrowIcon from '@assets/icons/arrow.svg';
import Lightbox from 'react-image-lightbox';
import { renderOptional } from '@shared/utils/render';
import { buildImgixSrc } from '@shared/utils/images';
import 'react-image-lightbox/style.css';

interface ImagesDetailProps {
  mainResource: DiagnosticsStepImages;
  nextResource: DiagnosticsStepImages;
  prevResource: DiagnosticsStepImages;
  onClose: () => void;
  onMoveNext: () => void;
  onMovePrev: () => void;
}

const ImagesDetail: FC<ImagesDetailProps> = ({
  mainResource,
  nextResource,
  prevResource,
  onClose,
  onMoveNext,
  onMovePrev,
}) => {
  const params = {
    auto: 'compress, format',
    w: 1500,
    q: 60,
  };

  const image = mainResource.resource != null ? mainResource.resource.url : undefined;
  const nextImage = nextResource != null && nextResource.resource != null ? nextResource.resource.url : undefined;
  const prevImage = prevResource != null && prevResource.resource != null ? prevResource.resource.url : undefined;

  return image ? (
    <Lightbox
      mainSrc={buildImgixSrc(image, params)}
      nextSrc={nextImage}
      prevSrc={prevImage}
      onCloseRequest={onClose}
      onMovePrevRequest={onMovePrev}
      onMoveNextRequest={onMoveNext}
    />
  ) : null;
};

interface DiagnosticsStepsImagesProps {
  images: Array<DiagnosticsStepImages>;
}

const DiagnosticsStepsImages: FC<DiagnosticsStepsImagesProps> = ({ images }) => {
  const [galleryIndex, setGalleryIndex] = useState<O.Option<number>>(O.none);

  useEffect(() => {
    const container = document.querySelector('.gallery-images');

    if (container) {
      const slide = new Glide('.gallery-images', {
        type: 'slider',
        startAt: 0,
        perView: 3,
        gap: 37,
        keyboard: false,
        rewind: false,
        breakpoints: {
          900: {
            perView: 2,
          },
          600: {
            perView: 1,
          },
        },
      });

      slide.mount();

      return () => {
        slide.destroy();
      };
    }
  }, []);

  const handleOpenGallery = (i: number) => () => setGalleryIndex(O.some(i));

  const handleCloseGallery = () => setGalleryIndex(O.none);

  const handleMovePrev = (i: number) => () => setGalleryIndex(O.some((i + images.length - 1) % images.length));

  const handleMoveNext = (i: number) => () => setGalleryIndex(O.some((i + 1) % images.length));

  return (
    <>
      <Styled.DiagnosticsStepsImagesContainer>
        {images.length > 3 ? (
          <div className="glide gallery-images">
            <div className="glide__track" data-glide-el="track">
              <ul className="glide__slides">
                {images.map((image, index) => (
                  <Styled.DiagnosticsStepsImagesSlide key={index} onClick={handleOpenGallery(index)}>
                    <div>
                      <Image
                        sizes={{
                          [`(max-width: 600px)`]: '100vw',
                          [`(max-width: 900px)`]: 'calc(100vw / 2)',
                          default: 'calc(1200px / 3)',
                        }}
                        src={image.resource}
                        imgixParams={{ ar: '16:9', fit: 'fill', fill: 'solid' }}
                      />
                    </div>
                  </Styled.DiagnosticsStepsImagesSlide>
                ))}
              </ul>
            </div>

            <div className="glide__arrows" data-glide-el="controls">
              <Styled.DiagnosticsStepsImagesArrowLeft data-glide-dir="<">
                <NextImage src={arrowIcon.src} width={10} height={17} />
              </Styled.DiagnosticsStepsImagesArrowLeft>

              <Styled.DiagnosticsStepsImagesArrowRight data-glide-dir=">">
                <NextImage src={arrowIcon.src} width={10} height={17} />
              </Styled.DiagnosticsStepsImagesArrowRight>
            </div>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <Styled.DiagnosticsStepsItems length={images.length}>
              {images.map((item, index) => (
                <Styled.DiagnosticsStepsItem onClick={handleOpenGallery(index)} key={index}>
                  <div>
                    <Image
                      sizes={{
                        [`(max-width: 1200px)`]: `calc(100vw / ${images.length})`,
                        default: `calc(1200px / ${images.length})`,
                      }}
                      src={item.resource}
                      imgixParams={{ ar: '16:9', fit: 'fill', fill: 'solid' }}
                    />
                  </div>
                </Styled.DiagnosticsStepsItem>
              ))}
            </Styled.DiagnosticsStepsItems>
          </div>
        )}
      </Styled.DiagnosticsStepsImagesContainer>

      {renderOptional(galleryIndex, index => (
        <ImagesDetail
          mainResource={images[index]}
          prevResource={images[index - 1]}
          nextResource={images[index + 1]}
          onClose={handleCloseGallery}
          onMovePrev={handleMovePrev(index)}
          onMoveNext={handleMoveNext(index)}
        />
      ))}
    </>
  );
};

export default DiagnosticsStepsImages;
